'use client';

import { motion } from "motion/react";
import { Bell, Menu, PanelLeft, Search } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../app/components/ui/dropdown-menu";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../app/components/ui/command";
import { Button, Input } from "../ui";
import { api } from "../../lib/api";
import { useAuth } from "../../providers/AuthProvider";
import { ThemeToggle } from "./ThemeToggle";
import type { SidebarItem } from "./types";

function flattenNavForCommand(
  items: SidebarItem[],
  parentLabel?: string,
): Array<{ href: string; label: string; icon: LucideIcon }> {
  const out: Array<{ href: string; label: string; icon: LucideIcon }> = [];
  for (const it of items) {
    const label = parentLabel ? `${parentLabel} › ${it.label}` : it.label;
    if (it.href) {
      out.push({ href: it.href, label, icon: it.icon });
    }
    if (it.children?.length) {
      out.push(...flattenNavForCommand(it.children, it.href ? undefined : it.label));
    }
  }
  return out;
}

interface HeaderProps {
  title?: string;
  searchPlaceholder?: string;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
  sidebarItems?: SidebarItem[];
  onOpenMobileNav?: () => void;
}

export function Header({
  title,
  searchPlaceholder = "Search...",
  leftSlot,
  rightSlot,
  sidebarItems = [],
  onOpenMobileNav,
}: HeaderProps) {
  const { user, token, logout, impersonation, lastLoginAt, sessionStartedAt } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [adminNotifications, setAdminNotifications] = useState<
    Array<{ id: string; reference_id: string; message: string; created_at: string | null }>
  >([]);
  const [commandOpen, setCommandOpen] = useState(false);

  const navCommands = useMemo(() => flattenNavForCommand(sidebarItems), [sidebarItems]);

  useEffect(() => {
    if (user?.role !== "super_admin" || !token || impersonation) {
      setAdminNotifications([]);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const res = await api.admin.listNotifications({ token, perPage: 20 });
        if (!cancelled) setAdminNotifications(res.data);
      } catch {
        if (!cancelled) setAdminNotifications([]);
      }
    }
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [user?.role, token, impersonation]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (location.pathname === "/pos" || location.pathname.startsWith("/pos/")) return;
      e.preventDefault();
      setCommandOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [location.pathname]);

  const notificationMenu = (
    <>
      <DropdownMenuLabel>Profile updates</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {adminNotifications.length === 0 ? (
        <div className="px-2 py-3 text-sm text-muted-foreground">No notifications</div>
      ) : (
        adminNotifications.map((n) => (
          <DropdownMenuItem key={n.id} asChild>
            <Link
              to={`/admin/businesses/${encodeURIComponent(n.reference_id)}/profile`}
              className="cursor-pointer flex flex-col items-start gap-0.5"
            >
              <span className="text-sm font-medium">{n.message}</span>
              <span className="text-xs text-muted-foreground font-mono">Business #{n.reference_id}</span>
            </Link>
          </DropdownMenuItem>
        ))
      )}
    </>
  );

  return (
    <>
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen} title="Go to…" description="Search pages">
        <CommandInput placeholder={searchPlaceholder} />
        <CommandList>
          <CommandEmpty>No results.</CommandEmpty>
          <CommandGroup heading="Pages">
            {navCommands.map((cmd) => {
              const Icon = cmd.icon;
              return (
                <CommandItem
                  key={cmd.href}
                  value={`${cmd.label} ${cmd.href}`}
                  onSelect={() => {
                    setCommandOpen(false);
                    navigate(cmd.href);
                  }}
                >
                  <Icon className="size-4 shrink-0" />
                  <span>{cmd.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      <header className="h-16 bg-card border-b border-border flex items-center justify-between gap-2 px-3 md:px-6 sticky top-0 z-10 shrink-0">
        <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden shrink-0 min-h-9 min-w-9 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
            onClick={() => onOpenMobileNav?.()}
            aria-label="Open menu"
          >
            <PanelLeft className="h-5 w-5" />
          </Button>
          {title ? <h1 className="text-base md:text-lg font-semibold shrink-0 truncate">{title}</h1> : null}
          <div className="hidden lg:block flex-1 max-w-xl min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                className="pl-10 bg-input-background border-0 min-h-9"
              />
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="lg:hidden shrink-0 min-h-9 min-w-9 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
            onClick={() => setCommandOpen(true)}
            aria-label="Search and go to page"
          >
            <Search className="h-5 w-5" />
          </Button>
          {leftSlot}
        </div>

        <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
          {rightSlot}
          {user ? (
            <div className="hidden lg:flex flex-col items-end text-xs text-muted-foreground max-w-[240px]">
              {impersonation ? (
                <span className="text-amber-900 font-medium text-right leading-tight">
                  Acting as admin: {impersonation.actorEmail}
                </span>
              ) : null}
              <span className="text-foreground font-medium text-right leading-tight">{user.name}</span>
              <span className="text-right leading-tight truncate">{user.email}</span>
              {user.role === "super_admin" && !impersonation ? (
                <>
                  {lastLoginAt ? (
                    <span className="text-right">Last login: {new Date(lastLoginAt).toLocaleString()}</span>
                  ) : null}
                  {sessionStartedAt ? (
                    <span className="text-right">Session: {new Date(sessionStartedAt).toLocaleString()}</span>
                  ) : null}
                  <Link to="/admin/profile" className="text-primary hover:underline text-right">
                    Profile
                  </Link>
                </>
              ) : null}
            </div>
          ) : null}
          {user ? (
            <Button variant="outline" onClick={logout} className="hidden sm:inline-flex min-h-9">
              Logout
            </Button>
          ) : null}

          <div className="hidden md:flex items-center gap-1.5 md:gap-3">
            <ThemeToggle />
            {user?.role === "super_admin" && !impersonation ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button variant="ghost" size="icon" className="relative min-h-9 min-w-9" type="button" aria-label="Notifications">
                      <Bell className="w-5 h-5" />
                      {adminNotifications.length > 0 ? (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold bg-destructive text-destructive-foreground rounded-full">
                          {adminNotifications.length > 99 ? "99+" : adminNotifications.length}
                        </span>
                      ) : null}
                    </Button>
                  </motion.div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  {notificationMenu}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="ghost" size="icon" className="relative min-h-9 min-w-9" type="button" aria-label="Notifications">
                  <Bell className="w-5 h-5" />
                </Button>
              </motion.div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="md:hidden min-h-9 min-w-9 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
                aria-label="More options"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Appearance</DropdownMenuLabel>
              <div className="flex justify-center px-2 py-2">
                <ThemeToggle />
              </div>
              {user?.role === "super_admin" && !impersonation ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  {adminNotifications.length === 0 ? (
                    <div className="px-2 py-2 text-sm text-muted-foreground">No notifications</div>
                  ) : (
                    adminNotifications.slice(0, 8).map((n) => (
                      <DropdownMenuItem key={n.id} asChild>
                        <Link
                          to={`/admin/businesses/${encodeURIComponent(n.reference_id)}/profile`}
                          className="cursor-pointer line-clamp-2"
                        >
                          {n.message}
                        </Link>
                      </DropdownMenuItem>
                    ))
                  )}
                </>
              ) : null}
              {user ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout()} className="sm:hidden">
                    Log out
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
}
